import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from '@tanium/react-emotion';
import { useLocation } from 'react-router-dom';
import { css } from '@tanium/style-system';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { Well } from '@tanium/react-well';
import { useTranslation } from 'react-i18next';
import { Page, PageHeader } from '@tanium/react-page';
import { useCurrentRoute } from '../../components/AppContent/useCurrentRoute';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useIframeMessageHandler } from '../../hooks/useIframeMessageHandler';
import { components as reactSelectComponents } from 'react-select';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { CloudProvider, InstanceStatus, TaniumInstance } from '../../types';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { getCMPLink } from '../../utilities/instanceHelper';
import { useTokenReloaderEffect } from '../../auth/useTokenReloaderCallbacks';
import { useAuthContext } from '../../auth';
import { AmpAccessToken } from '../../auth/Auth';
import { TaniumSelect } from '../../models/TaniumSelectModels';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const IframeContainer = styled(Box)(
  css({
    overflow: 'hidden',
    height: '100%',
  })
);

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const Container = styled(Box)(
  css({
    width: '50%',
    alignItems: 'center',
    display: 'flex',
    whiteSpace: 'nowrap',
  })
);

type SelectOption = { label: string; value: TaniumInstance; isDisabled?: boolean; disabledTooltip?: string };

const CMP_DISABLED_STATUSES = new Set<InstanceStatus | undefined>([
  InstanceStatus.Creating,
  InstanceStatus.Deleted,
]);

const { Option: DefaultOption } = reactSelectComponents;

const DisabledInstanceOption = (props: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const disabledTooltip = props.isDisabled ? (props.data as SelectOption).disabledTooltip : undefined;
  if (!disabledTooltip) {
    return <DefaultOption {...props} />;
  }
  return (
    <SimpleTooltip content={disabledTooltip} placement="right">
      {({ ref }: { ref: React.Ref<HTMLDivElement> }) => (
        <div ref={ref} style={{ pointerEvents: 'all' }}>
          <DefaultOption {...props} />
        </div>
      )}
    </SimpleTooltip>
  );
};

export const CMP: React.VFC = () => {
  const { t } = useTranslation();
  const auth = useAuthContext();
  const currentRoute = useCurrentRoute();
  const location = useLocation();
  const [selectedInstance, setSelectedInstance] = React.useState<SelectOption>();
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const [authToken, setAuthToken] = React.useState<string>();
  const postTokenIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPostTokenInterval = useCallback(() => {
    if (postTokenIntervalRef.current !== null) {
      clearInterval(postTokenIntervalRef.current);
      postTokenIntervalRef.current = null;
    }
  }, []);

  const { linkedTaniumInstances, loadLinkedTaniumInstances } = useTaniumInstances();

  const selectOptions: SelectOption[] = useMemo(() => {
    return linkedTaniumInstances
      ? linkedTaniumInstances
        .filter((instance) => !!instance.cloudProvider)
        .map((instance) => {
          const isDisabled = CMP_DISABLED_STATUSES.has(instance.status);
          return {
            label: instance.url,
            value: instance,
            isDisabled,
            disabledTooltip: isDisabled
              ? t('Instance in state state is not available', { state: instance.status })
              : undefined,
          };
        })
      : [];
  }, [linkedTaniumInstances]);

  useEffect(() => {
    if (selectedInstance) return;
    if (location.search.startsWith('?id=') && selectedInstance === undefined) {
      const instanceId = location.search.substring(4);
      setSelectedInstance(selectOptions.find((option) => option.value.id === instanceId));
    } else if (selectOptions.length > 0) {
      setSelectedInstance(selectOptions[0]);
    }
  }, [location.search, selectOptions, selectedInstance]);

  const instanceUrl = useMemo(() => {
    if (!selectedInstance) return '';
    if (window.location.origin.startsWith('http://localhost')) {
      return `http://localhost:3000/#/${currentRoute!}`;
    }

    const { url, cloudProvider } = selectedInstance.value;
    return getCMPLink(url, cloudProvider!, currentRoute);
  }, [currentRoute, selectedInstance]);

  const instanceOrigin = useMemo(() => {
    if (!instanceUrl) return '';
    try {
      return new URL(instanceUrl).origin;
    } catch {
      return '';
    }
  }, [instanceUrl]);

  // Keyed on origin, not URL: same-origin route changes are fragment navs with no load event.
  useEffect(() => {
    setIframeLoaded(false);
    return clearPostTokenInterval;
  }, [instanceOrigin, clearPostTokenInterval]);

  // TODO(TSP-1778): Remove when CMP is explicitly requesting token
  const tokenCallback = useCallback(
    (token: AmpAccessToken) => {
      if (!instanceUrl) {
        return false;
      }
      if (!iframeLoaded) {
        return false;
      }
      const iframe = document.querySelector('iframe');
      const handle = iframe!.contentWindow;
      if (!handle) {
        return false;
      }

      // NOTE, the following is a hack, where the content security policy will block
      // AMP from checking the origin once the iframe content has loaded.
      // postMessage is asynchronous and cannot catch an error, so this gives a
      // quick way to ensure that the iframe is actually loaded.
      try {
        return handle.origin === instanceUrl;
      } catch {
        // no-op
      }

      const message = {
        jwt: token.token,
      };
      let times = 0;
      clearPostTokenInterval();
      postTokenIntervalRef.current = setInterval(() => {
        handle.postMessage(message, instanceUrl);
        times++;
        // 20 times per second for 10 seconds
        if (times >= 20 * 10) {
          clearPostTokenInterval();
        }
      }, 50);
      if (token.token !== authToken) {
        setAuthToken(token.token);
      }
      return true;
    },
    [instanceUrl, iframeLoaded, authToken, clearPostTokenInterval]
  );

  useTokenReloaderEffect(auth, tokenCallback);

  const instanceDomain = selectedInstance?.value.url || '';
  const instanceCloudProvider = selectedInstance?.value.cloudProvider;
  const iframeMessageOrigin =
    (instanceCloudProvider === CloudProvider.AZURE || instanceCloudProvider === CloudProvider.Gov_Azure) ? `https://${instanceDomain}` : `https://portal.${instanceDomain}`;
  useIframeMessageHandler(iframeMessageOrigin, authToken);

  return (
    <IframeContainer>
      <Page header={<PageHeader heading={''} />}>
        <Well>
          <AsyncSuspense
            state={loadLinkedTaniumInstances}
            loading={
              <Box mt={2} height={1} width="100%">
                <Shimmer />
              </Box>
            }
            failure={t('Failed to load Tanium Instances')}
          >
            <Box width="100%">
              <Container>
                <Box mx={1}>{t('Instance Hostname:')}</Box>
                <Box mx={1}>
                  <TaniumSelect
                    aria-label={t('Event')}
                    options={selectOptions}
                    isMulti={false}
                    value={selectedInstance}
                    size="sm"
                    styles={{ container: (base: Record<string, unknown>) => ({ ...base, width: 200 }) }}
                    components={{ Option: DisabledInstanceOption }}
                    onChange={(option: SelectOption) => {
                      setSelectedInstance(option);
                    }}
                  />
                </Box>
                {selectedInstance && selectedInstance.value && (
                  <>
                    <Box mx={1}>{t('License ID:')}</Box>
                    <Box mx={1}>{selectedInstance?.value?.licenseId}</Box>
                  </>
                )}
              </Container>
            </Box>
          </AsyncSuspense>
        </Well>
        <iframe
          id="iframe"
          title="CMP"
          src={instanceUrl}
          width="100%"
          style={{
            marginTop: '15px',
            border: 'none',
            height: '100%',
          }}
          onLoad={() => {
            setIframeLoaded(true);
          }}
        />
      </Page>
    </IframeContainer>
  );
};
